import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Icon as IconifyIcon } from "@iconify/react";
import {
  Server, Database, Cloud, Shield, Cpu, Monitor, Network, Box,
  HelpCircle, Code2, AlertTriangle, Layers, ExternalLink,
  Workflow, Split, GitBranch, GitMerge, GitFork, Merge, RotateCw,
  HardDrive, Archive, FolderOpen, Boxes, ServerCrash, Radio,
  Terminal, Bug, Settings, TestTube, Rocket, Package, Puzzle,
  MessageSquare, Bell, Zap, Webhook, Globe,
  LayoutDashboard, Component, FileText, FormInput, Tablet,
  User, Users, UserCheck, Building2, ShieldCheck,
  StickyNote, Info, Link, BookOpen, Target, Crosshair,
  CheckCircle, XCircle, Clock, Timer, Play, Pause,
  Brain, Computer, Waypoints, Disc3, Router, BrickWall, EthernetPort,
  type LucideIcon as LucideIconType,
} from "lucide-react";
import { THEME, NODE_COLOR_PALETTE } from "@/config/theme";
import { CV } from "@/config/canvasSizing";
import { getBrandColor } from "@/config/iconCatalog";
import { type NodePageSummary } from "@/store/useNodePageStore";

// ── Types ─────────────────────────────────────────────────────────────────────

export type NodeType = string;

export type { NodePageSummary };

export interface NodeCardProps {
  type: NodeType;
  label: string;
  icon?: string;
  color?: string;
  selected?: boolean;
  enlightened?: boolean; // New prop
  revealHighlight?: boolean;
  /** Hand-tool click-to-focus: gives the node the "alive" selected treatment
      (amber pulse glow + scale + badge pulse) without the arrow-tool accent ring —
      the yellow ring comes from revealHighlight instead. */
  focused?: boolean;
  hasDrillTarget?: boolean;
  /** Loaded page list. Undefined = not fetched yet; falls back to pageCount. */
  pages?: NodePageSummary[];
  /** Page count from the canvas payload — known on first paint, no fetch. */
  pageCount?: number;
  prefetching?: boolean;
  isEditingLabel?: boolean;
  onBadgeClick?: () => void;
  onLabelCommit?: (label: string) => void;
}

// ── Config ────────────────────────────────────────────────────────────────────

interface TypeConfig {
  containerClass: string;
  iconClass: string;
  labelClass: string;
  defaultIcon: LucideIconType;
}

const L  = THEME.node.default;
const LD = THEME.node.label;

const TYPE_CONFIG: Record<string, TypeConfig> = {
  // ── Core ──────────────────────────────────────────────────────────────────
  // Accent hues avoid the blue/purple family — the indigo selection ring and
  // enlightenment glow already own it, and same-hue kickers disappear into it.
  concept: {
    containerClass: L,
    iconClass: "text-lime-400",
    labelClass: LD,
    defaultIcon: Box,
  },
  question: {
    containerClass: THEME.node.dashedBorder,
    iconClass: "text-rose-400",
    labelClass: LD,
    defaultIcon: HelpCircle,
  },
  example: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: `${LD} font-mono`,
    defaultIcon: Code2,
  },
  warning: {
    containerClass: L,
    iconClass: "text-amber-500",
    labelClass: LD,
    defaultIcon: AlertTriangle,
  },
  "deep-dive": {
    containerClass: THEME.node.dashedBorder,
    iconClass: "text-teal-400",
    labelClass: LD,
    defaultIcon: Layers,
  },
  // ── Process / Flow ────────────────────────────────────────────────────────
  process: {
    containerClass: L,
    iconClass: "text-cyan-500",
    labelClass: LD,
    defaultIcon: Workflow,
  },
  decision: {
    containerClass: L,
    iconClass: "text-yellow-400",
    labelClass: LD,
    defaultIcon: Split,
  },
  fork: {
    containerClass: L,
    iconClass: "text-cyan-500",
    labelClass: LD,
    defaultIcon: GitFork,
  },
  merge: {
    containerClass: L,
    iconClass: "text-cyan-500",
    labelClass: LD,
    defaultIcon: Merge,
  },
  loop: {
    containerClass: L,
    iconClass: "text-cyan-500",
    labelClass: LD,
    defaultIcon: RotateCw,
  },
  branch: {
    containerClass: L,
    iconClass: "text-cyan-500",
    labelClass: LD,
    defaultIcon: GitBranch,
  },
  gateway: {
    containerClass: L,
    iconClass: "text-yellow-400",
    labelClass: LD,
    defaultIcon: GitMerge,
  },
  // ── Data / Storage ────────────────────────────────────────────────────────
  data: {
    containerClass: L,
    iconClass: "text-emerald-500",
    labelClass: LD,
    defaultIcon: Database,
  },
  database: {
    containerClass: L,
    iconClass: "text-emerald-500",
    labelClass: LD,
    defaultIcon: Database,
  },
  server: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Server,
  },
  storage: {
    containerClass: L,
    iconClass: "text-emerald-500",
    labelClass: LD,
    defaultIcon: HardDrive,
  },
  disk: {
    containerClass: L,
    iconClass: "text-emerald-500",
    labelClass: LD,
    defaultIcon: Disc3,
  },
  volume: {
    containerClass: L,
    iconClass: "text-emerald-500",
    labelClass: LD,
    defaultIcon: HardDrive,
  },
  cache: {
    containerClass: L,
    iconClass: "text-emerald-500",
    labelClass: LD,
    defaultIcon: Archive,
  },
  queue: {
    containerClass: L,
    iconClass: "text-orange-500",
    labelClass: LD,
    defaultIcon: Boxes,
  },
  cloud: {
    containerClass: L,
    iconClass: "text-sky-500",
    labelClass: LD,
    defaultIcon: Cloud,
  },
  // ── Code / Dev ────────────────────────────────────────────────────────────
  code: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: `${LD} font-mono`,
    defaultIcon: Code2,
  },
  terminal: {
    containerClass: THEME.node.terminal,
    iconClass: "text-green-400",
    labelClass: THEME.node.terminalLabel,
    defaultIcon: Terminal,
  },
  bug: {
    containerClass: L,
    iconClass: "text-red-500",
    labelClass: LD,
    defaultIcon: Bug,
  },
  config: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Settings,
  },
  test: {
    containerClass: L,
    iconClass: "text-teal-500",
    labelClass: LD,
    defaultIcon: TestTube,
  },
  deploy: {
    containerClass: L,
    iconClass: "text-sky-500",
    labelClass: LD,
    defaultIcon: Rocket,
  },
  package: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Package,
  },
  module: {
    containerClass: L,
    iconClass: "text-orange-400",
    labelClass: LD,
    defaultIcon: Puzzle,
  },
  // ── Infrastructure ────────────────────────────────────────────────────────
  network: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Network,
  },
  router: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Router,
  },
  switch: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: EthernetPort,
  },
  firewall: {
    containerClass: L,
    iconClass: "text-red-500",
    labelClass: LD,
    defaultIcon: BrickWall,
  },
  cpu: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Cpu,
  },
  monitor: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Monitor,
  },
  computer: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Computer,
  },
  shield: {
    containerClass: L,
    iconClass: "text-red-500",
    labelClass: LD,
    defaultIcon: Shield,
  },
  "load-balancer": {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Waypoints,
  },
  // ── Communication / Events ────────────────────────────────────────────────
  message: {
    containerClass: L,
    iconClass: "text-emerald-400",
    labelClass: LD,
    defaultIcon: MessageSquare,
  },
  event: {
    containerClass: L,
    iconClass: "text-yellow-500",
    labelClass: LD,
    defaultIcon: Zap,
  },
  webhook: {
    containerClass: L,
    iconClass: "text-orange-500",
    labelClass: LD,
    defaultIcon: Webhook,
  },
  notification: {
    containerClass: L,
    iconClass: "text-yellow-400",
    labelClass: LD,
    defaultIcon: Bell,
  },
  api: {
    containerClass: L,
    iconClass: "text-fuchsia-500",
    labelClass: `${LD} font-mono`,
    defaultIcon: Globe,
  },
  // ── UI / Interface ────────────────────────────────────────────────────────
  screen: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Tablet,
  },
  component: {
    containerClass: L,
    iconClass: "text-orange-400",
    labelClass: LD,
    defaultIcon: Component,
  },
  page: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: LayoutDashboard,
  },
  form: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: FormInput,
  },
  // ── People / Org ──────────────────────────────────────────────────────────
  actor: {
    containerClass: L,
    iconClass: "text-rose-500",
    labelClass: LD,
    defaultIcon: User,
  },
  user: {
    containerClass: L,
    iconClass: "text-rose-500",
    labelClass: LD,
    defaultIcon: User,
  },
  team: {
    containerClass: L,
    iconClass: "text-rose-500",
    labelClass: LD,
    defaultIcon: Users,
  },
  org: {
    containerClass: L,
    iconClass: "text-rose-500",
    labelClass: LD,
    defaultIcon: Building2,
  },
  role: {
    containerClass: L,
    iconClass: "text-rose-500",
    labelClass: LD,
    defaultIcon: UserCheck,
  },
  auth: {
    containerClass: L,
    iconClass: "text-red-500",
    labelClass: LD,
    defaultIcon: ShieldCheck,
  },
  brain: {
    containerClass: L,
    iconClass: "text-pink-400",
    labelClass: LD,
    defaultIcon: Brain,
  },
  // ── Reference / Info ──────────────────────────────────────────────────────
  note: {
    containerClass: THEME.node.note,
    iconClass: "text-amber-500",
    labelClass: THEME.node.noteLabel,
    defaultIcon: StickyNote,
  },
  info: {
    containerClass: L,
    iconClass: "text-sky-500",
    labelClass: LD,
    defaultIcon: Info,
  },
  reference: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: BookOpen,
  },
  link: {
    containerClass: L,
    iconClass: "text-cyan-500",
    labelClass: LD,
    defaultIcon: Link,
  },
  source: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: FileText,
  },
  target: {
    containerClass: L,
    iconClass: "text-emerald-500",
    labelClass: LD,
    defaultIcon: Target,
  },
  goal: {
    containerClass: L,
    iconClass: "text-emerald-500",
    labelClass: LD,
    defaultIcon: Crosshair,
  },
  // ── Status ────────────────────────────────────────────────────────────────
  success: {
    containerClass: L,
    iconClass: "text-green-500",
    labelClass: LD,
    defaultIcon: CheckCircle,
  },
  error: {
    containerClass: L,
    iconClass: "text-red-500",
    labelClass: LD,
    defaultIcon: XCircle,
  },
  // ── Time / Control ────────────────────────────────────────────────────────
  timer: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Timer,
  },
  cron: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Clock,
  },
  trigger: {
    containerClass: L,
    iconClass: "text-yellow-500",
    labelClass: LD,
    defaultIcon: Play,
  },
  pause: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: Pause,
  },
  // ── Service mesh ──────────────────────────────────────────────────────────
  microservice: {
    containerClass: L,
    iconClass: "text-fuchsia-500",
    labelClass: LD,
    defaultIcon: ServerCrash,
  },
  pubsub: {
    containerClass: L,
    iconClass: "text-orange-500",
    labelClass: LD,
    defaultIcon: Radio,
  },
  folder: {
    containerClass: L,
    iconClass: "text-slate-400",
    labelClass: LD,
    defaultIcon: FolderOpen,
  },
};

const ICON_MAP: Record<string, LucideIconType> = {
  server:   Server,
  database: Database,
  cloud:    Cloud,
  shield:   Shield,
  cpu:      Cpu,
  monitor:  Monitor,
  network:  Network,
  brain:    Brain,
  computer: Computer,
};


// Icon-tint wrap background per icon color (design mock). Full literal class
// strings so Tailwind's scanner emits them. Keyed by the `text-*` icon class so
// both the type default (TYPE_CONFIG.iconClass) and the color override resolve.
const ICON_TINT: Record<string, string> = {
  // -400 entries are the type accents; -500 blues/purples stay for the manual
  // color-override palette (NODE_COLOR_PALETTE), which still offers them.
  "text-lime-400":    "bg-lime-400/10",
  "text-rose-400":    "bg-rose-400/10",
  "text-teal-400":    "bg-teal-400/10",
  "text-yellow-400":  "bg-yellow-400/10",
  "text-orange-400":  "bg-orange-400/10",
  "text-pink-400":    "bg-pink-400/10",
  "text-slate-400":   "bg-slate-400/10",
  "text-emerald-400": "bg-emerald-400/10",
  "text-indigo-500":  "bg-indigo-500/10",
  "text-indigo-400":  "bg-indigo-400/10",
  "text-violet-500":  "bg-violet-500/10",
  "text-purple-500":  "bg-purple-500/10",
  "text-cyan-500":    "bg-cyan-500/10",
  "text-sky-500":     "bg-sky-500/10",
  "text-blue-500":    "bg-blue-500/10",
  "text-emerald-500": "bg-emerald-500/10",
  "text-green-500":   "bg-green-500/10",
  "text-green-400":   "bg-green-400/10",
  "text-teal-500":    "bg-teal-500/10",
  "text-amber-500":   "bg-amber-500/10",
  "text-yellow-500":  "bg-yellow-500/10",
  "text-orange-500":  "bg-orange-500/10",
  "text-red-500":     "bg-red-500/10",
  "text-rose-500":    "bg-rose-500/10",
  "text-fuchsia-500": "bg-fuchsia-500/10",
  "text-slate-500":   "bg-slate-500/10",
};
function iconTintClass(iconClass: string): string {
  return ICON_TINT[iconClass] ?? "bg-indigo-500/10";
}

// Human-readable kicker derived from the node `type`. Most types title-case
// cleanly; a few need explicit forms (acronyms, the mock's "Deep dive").
const TYPE_LABEL: Record<string, string> = {
  "deep-dive":     "Deep dive",
  api:             "API",
  cpu:             "CPU",
  "load-balancer": "Load balancer",
  pubsub:          "Pub/Sub",
};
function humanizeType(type: string): string {
  return (
    TYPE_LABEL[type] ??
    type.split(/[-_ ]+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ")
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function NodeCard(props: NodeCardProps) {
  const {
    type,
    label,
    icon,
    color,
    selected = false,
    revealHighlight = false,
    focused = false,
    hasDrillTarget = false,
    pages,
    pageCount,
    prefetching = false,
    isEditingLabel = false,
    onBadgeClick,
    onLabelCommit,
  } = props;
  // A loaded page list is authoritative — it reflects live create/delete. Until
  // one exists, fall back to the count the canvas payload already gave us, so
  // the badge is right on first paint instead of waiting for a second fetch.
  const badgeCount = pages !== undefined ? pages.length : (pageCount ?? 0);
  const showShimmer = prefetching && badgeCount === 0;
  const cfg = TYPE_CONFIG[type] ?? TYPE_CONFIG.concept;
  const enlightened = props.enlightened ?? true;
  const colorOverride = NODE_COLOR_PALETTE.find((c) => c.name === color);
  const iconClass = colorOverride?.iconClass ?? cfg.iconClass;
  const isIconifyId = icon?.includes(":");
  const LucideIcon: LucideIconType = icon && !isIconifyId
    ? (ICON_MAP[icon] ?? cfg.defaultIcon)
    : cfg.defaultIcon;

  const [editValue, setEditValue] = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditingLabel) {
      setEditValue(label);
      requestAnimationFrame(() => inputRef.current?.select());
    }
  }, [isEditingLabel, label]);

  function commitEdit() {
    const trimmed = editValue.trim() || label;
    onLabelCommit?.(trimmed);
  }

  return (
    // Enlightenment: an enlightened node is the light source — it casts an
    // indigo halo (box-shadow bloom) onto the dark canvas; an unlit node keeps
    // only its black elevation shadow. Opacity/scale on reveal are owned by the
    // wrapper motion.div in Viewport.tsx, so NodeCard owns only the glow and the
    // two animations don't fight. `initial={false}` means no mount animation: a
    // lit node paints its glow immediately (no flicker during normal editing).
    <motion.div
      data-enlightened={enlightened}
      initial={false}
      // Selected node (design mock): scales up slightly and its indigo halo
      // pulses (lit ↔ active loop), matching the mock's `tt-glowpulse`. Otherwise
      // it holds the static enlightenment glow.
      animate={{
        boxShadow: (selected || focused)
          ? [THEME.raw.nodeGlowLit, THEME.raw.nodeGlowActive, THEME.raw.nodeGlowLit]
          : enlightened ? THEME.raw.nodeGlowLit : THEME.raw.nodeGlowUnlit,
        scale: (selected || focused) ? 1.03 : 1,
      }}
      transition={{
        boxShadow: (selected || focused)
          ? { duration: 2.6, ease: "easeInOut", repeat: Infinity }
          : { duration: 0.5, ease: "easeOut" },
        scale: { duration: 0.25, ease: "easeOut" },
      }}
      style={{ borderRadius: CV.node.radius, paddingLeft: CV.node.padX, paddingRight: CV.node.padX, gap: CV.node.gap }}
      className={`
        w-full h-full relative
        flex items-center
        ${cfg.containerClass}
        ${selected ? "!border-indigo-400/90 !border-dashed" : ""}
        ${revealHighlight ? "ring-2 ring-yellow-400" : ""}
      `}
    >
      {/* Icon-tint wrap: the icon sits in a rounded box filled with its accent
          hue at low alpha (design mock). */}
      <div
        data-testid="node-icon-wrap"
        className={`shrink-0 flex items-center justify-center ${iconTintClass(iconClass)}`}
        style={{ width: CV.node.iconWrapSize, height: CV.node.iconWrapSize, borderRadius: CV.node.iconWrapRadius }}
      >
        {/* logos:* SVGs carry their own brand colors and ignore currentColor;
            every other icon (lucide, mdi:*, simple-icons:*) is monochrome and
            takes the accent class. Brand hexes from getBrandColor still win —
            the explicit color prop overrides the inherited class. */}
        <div className={icon?.startsWith("logos:") ? "" : iconClass}>
          {isIconifyId && icon
            ? <IconifyIcon icon={icon} width={CV.node.iconInWrapSize} height={CV.node.iconInWrapSize} color={getBrandColor(icon)} />
            : <LucideIcon size={CV.node.iconInWrapSize} />
          }
        </div>
      </div>

      <div className="min-w-0 flex-1 cursor-text">
        {/* Type kicker (mono, uppercase, tracked) above the node label. */}
        {/* Kicker takes the node accent so the type reads in color; note keeps
            the muted gray — its amber sticky container already carries the hue. */}
        <p data-testid="node-type-kicker" className={`text-[9px] leading-none truncate ${THEME.node.typeLabel} ${type === "note" ? "text-zinc-300" : iconClass}`}>{humanizeType(type)}</p>
        {isEditingLabel ? (
          <input
            ref={inputRef}
            className={`w-full bg-transparent text-[13px] font-bold outline-none ${cfg.labelClass} nodrag nopan`}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onBlur={commitEdit}
            onKeyDown={(e) => {
              if (e.key === "Enter") { e.preventDefault(); commitEdit(); }
              if (e.key === "Escape") { onLabelCommit?.(label); }
            }}
          />
        ) : (
          <p className={`text-[13px] font-bold leading-tight truncate ${cfg.labelClass}`}>{label}</p>
        )}
      </div>

      {type === "question" && (
        <span
          className="absolute top-1 left-1.5 text-[9px] font-bold text-rose-400 leading-none"
          aria-hidden="true"
        >
          ?
        </span>
      )}

      {type === "deep-dive" && hasDrillTarget && (
        <div
          className="absolute bottom-1 left-1.5 text-indigo-300/80"
          data-testid="portal-badge"
        >
          <ExternalLink size={10} />
        </div>
      )}

      {/* Info badge. Sits inline at the node's right edge (per design mock):
          file-text glyph + page count, muted when idle and indigo (pulsing)
          when selected. Shimmer while prefetching pages. */}
      <div className="relative shrink-0 z-10">
        <button
          data-testid="node-info-badge"
          onClick={(e) => {
            e.stopPropagation();
            onBadgeClick?.();
          }}
          className={`flex items-center gap-[3px] transition-colors hover:text-canvas-accent ${
            showShimmer
              ? "text-canvas-muted animate-[kf-badge-shimmer_1.2s_ease-in-out_infinite]"
              : (selected || focused)
                ? "text-indigo-300 animate-[kf-badge_1.6s_ease-in-out_infinite]"
                : "text-canvas-muted"
          }`}
        >
          <FileText size={CV.node.infoIconSize} />
          {showShimmer ? (
            <span data-testid="node-page-shimmer" className="inline-block w-3 h-2.5 rounded-sm bg-canvas-muted/30 animate-pulse" />
          ) : badgeCount > 0 ? (
            <span data-testid="node-page-count" className="text-[10px] font-mono font-semibold leading-none">
              {badgeCount}
            </span>
          ) : null}
        </button>
      </div>
    </motion.div>
  );
}
